import React from 'react';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  ButtonBase,
  makeStyles,
  IconButton,
  Theme,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { TrashWhiteBackgroundIcon } from '../SvgIcons';
import { successCircle } from '../../../svg';

type Props = {
  deleteButtonId?: string;
  imageBase64: string;
  onDelete?: () => void;
  disabled?: boolean;
  requestStatus: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  '@keyframes successUpload': {
    '0%': {
      /** In firefox we need to rotate it 0.0001deg so it does not cause pixel snapping
       * https://stackoverflow.com/questions/24854640/strange-pixel-shifting-jumping-in-firefox-with-css-transitions
       */
      transform: 'scale(.25,0.25) rotate(0.0001deg)',
    },
    '50%': {
      transform: 'scale(1, 1)',
    },
    '75%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0,
    },
  },
  '@keyframes backdropSuccess': {
    '0%': {
      filter: 'brightness(0.5)',
    },
    '75%': {
      filter: 'brightness(0.5)',
    },
    '100%': {
      filter: 'none',
    },
  },
  '@keyframes displayEffect': {
    '0%': {
      opacity: 0,
      transform: 'scale(0)',
    },
    '85%': {
      opacity: 0,
      transform: 'scale(0)',
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 1,
    },
  },
  container: {
    position: 'relative',
    width: '100%',
  },
  image: {
    width: '100%',
    height: 300,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    overflow: 'hidden',
    borderRadius: 10,
  },
  trashIcon: {
    position: 'absolute',
    zIndex: 1,
    top: theme.spacing(2),
    right: theme.spacing(2),
    cursor: 'pointer',
  },
  loadingIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  successEffect: {
    animation: `$successUpload 1000ms ${theme.transitions.easing.easeInOut}`,
    animationFillMode: 'forwards',
  },
  backdropEffect: {
    animation: `$backdropSuccess 1000ms ${theme.transitions.easing.sharp}`,
    animationFillMode: 'forwards',
  },
  requestingEffect: {
    filter: 'blur(1px) brightness(0.5)',
  },
  displayEffect: {
    animation: `$displayEffect 1000ms ${theme.transitions.easing.sharp}`,
    animationFillMode: 'forwards',
  },
}));

export const returnCorrectAnimation = (
  isRequesting: boolean,
  succeed: boolean,
  classes: ClassNameMap,
): string => {
  if (isRequesting) {
    return classes.requestingEffect;
  }
  if (succeed) {
    return classes.backdropEffect;
  }
  return '';
};

const ImagePreview = ({
  imageBase64,
  onDelete = null,
  deleteButtonId = '',
  disabled = false,
  requestStatus,
}: Props): JSX.Element => {
  const classes = useStyles();
  const success = requestStatus === 'success';
  const isRequesting = requestStatus === 'requesting';
  return (
    <div className={classes.container}>
      <ButtonBase
        data-testid="image-preview"
        className={`${classes.image} ${returnCorrectAnimation(
          isRequesting,
          success,
          classes,
        )}`}
        disableRipple
        disabled={disabled}
        style={{
          backgroundImage: `url(${imageBase64})`,
        }}
      />
      {isRequesting && (
        <Box className={classes.loadingIcon} data-testid="loading-progress">
          <CircularProgress size={32} />
        </Box>
      )}
      {success && (
        <Box className={`${classes.loadingIcon}`} data-testid="success-icon">
          <img
            src={successCircle}
            alt="success-upload"
            className={classes.successEffect}
          />
        </Box>
      )}
      {onDelete !== null && !isRequesting && (
        <IconButton
          id={deleteButtonId}
          data-testid="delete-button"
          className={`${classes.trashIcon} ${success && classes.displayEffect}`}
          onClick={onDelete}
          disabled={disabled}
        >
          <TrashWhiteBackgroundIcon />
        </IconButton>
      )}
    </div>
  );
};

export default ImagePreview;
